import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import Players from './Players';
import Partners from './Partners';
import EventCalendar from './EventCalendar';
import DashboardHome from './DashboardHome';
import UserDashboardHome from './UserDashboardHome';
import ScorerDashboardHome from './ScorerDashboardHome';
import EditorDashboardHome from './EditorDashboardHome';
import ManagerDashboardHome from './ManagerDashboardHome';
import TechnicalDashboardHome from './TechnicalDashboardHome';
import PlayerScores from './dashboard/PlayerScores';
import PressManagement from './dashboard/PressManagement';
import DashboardHeader from '../components/DashboardHeader';
import Contents from './Contents';
import Tournaments from './Tournaments';
import Settings from './Settings';
import Finance from './Finance';
import Analytics from './Analytics';
import Courses from './Courses';
import InterestSubmissions from './InterestSubmissions';
import Messaging from './Messaging';
import { getTournaments } from '../lib/supabase';
import { supabase } from '../lib/supabase';
import { toast } from 'react-hot-toast';
import { useCurrentUser } from '../hooks/useCurrentUser';
import type { AdminTab } from '../lib/rolePermissions';

interface Tournament {
  id: string;
  name: string;
  date: string;
  status: string;
}

const defaultTournament: Tournament = {
  id: '',
  name: 'No Tournaments',
  date: '',
  status: ''
};

function Dashboard() {
  const { user: currentUser, loading: userLoading } = useCurrentUser();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<Tournament>(defaultTournament);
  const [selectedPage, setSelectedPage] = useState('');
  const [loading, setLoading] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const [authStatus, setAuthStatus] = useState<'allowed' | 'pending' | 'denied'>('allowed');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      // Refresh session to ensure JWT is current before RLS queries
      await supabase.auth.refreshSession();
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/', { replace: true });
        return;
      }

      const { data } = await supabase
        .from('auth_users').select('role, is_active').eq('id', user.id).maybeSingle();

      if (data && data.is_active === false) {
        setAuthStatus('denied');
      } else {
        // Allow access if: row exists and is active, OR row is missing/unreadable
        // (trigger may have created it but RLS blocks the read).
        // Role-based tab restrictions are handled by useCurrentUser.
        setAuthStatus('allowed');
        fetchTournaments();
      }
    } catch {
      setAuthStatus('denied');
    } finally {
      setAuthChecking(false);
    }
  };


  useEffect(() => {
    const path = location.pathname.split('/');
    const tabFromPath = path.length > 2 && path[2] ? path[2] : 'dashboard';

    if (currentUser && currentUser.allowedTabs.length > 0 && !currentUser.allowedTabs.includes(tabFromPath as AdminTab)) {
      const firstAllowed = currentUser.allowedTabs[0];
      const redirectPath = firstAllowed === 'dashboard' ? '/dashboard' : `/dashboard/${firstAllowed}`;
      navigate(redirectPath, { replace: true });
    } else {
      setActiveTab(tabFromPath);
    }
  }, [location, currentUser]);

  useEffect(() => {
    const handler = (e: Event) => {
      const tab = (e as CustomEvent).detail;
      if (tab) handleTabChange(tab);
    };
    window.addEventListener('navigate-tab', handler);
    return () => window.removeEventListener('navigate-tab', handler);
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);

      const { data, error } = await getTournaments();

      if (error) throw error;

      if (data && data.length > 0) {
        const formattedTournaments = data.map(tournament => ({
          id: tournament.id,
          name: tournament.name,
          date: `${new Date(tournament.start_date).toLocaleDateString()} - ${new Date(tournament.end_date).toLocaleDateString()}`,
          status: tournament.status
        }));

        setTournaments(formattedTournaments);

        const tournamentToSelect = formattedTournaments.find(t =>
          t.status.toLowerCase() === 'upcoming'
        ) || formattedTournaments[0];

        setSelectedTournament(tournamentToSelect || defaultTournament);
      } else {
        setSelectedTournament(defaultTournament);
      }
    } catch (err) {
      console.error('Error fetching tournaments:', err);
      toast.error('Failed to load tournaments');
    } finally {
      setLoading(false);
    }
  };

  const handleTournamentChange = (tournament: Tournament) => {
    setSelectedTournament(tournament);
  };

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    if (tab === 'dashboard') {
      navigate('/dashboard');
    } else {
      navigate(`/dashboard/${tab}`);
    }
  };

  const handleBackToOverview = () => {
    setSelectedPage('');
  };

  const renderPlayerScoresSubTab = (currentTab: 'players' | 'scores') => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="flex">
          <button
            onClick={() => handleTabChange('players')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 ${
              currentTab === 'players'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            Players
          </button>
          <button
            onClick={() => handleTabChange('scores')}
            className={`flex-1 py-4 px-6 text-center font-medium border-b-2 ${
              currentTab === 'scores'
                ? 'border-golf-orange text-golf-orange'
                : 'border-transparent text-gray-500 hover:text-golf-gray hover:border-gray-300'
            }`}
          >
            Scores
          </button>
        </div>
      </div>
      {currentTab === 'players'
        ? <Players tournamentId={selectedTournament.id} />
        : <PlayerScores tournamentId={selectedTournament.id} tournamentName={selectedTournament.name} />
      }
    </div>
  );

  const renderContent = () => {
    if (
      currentUser &&
      !currentUser.allowedTabs.includes(activeTab as AdminTab)
    ) {
      const firstAllowed = currentUser.allowedTabs[0];
      if (!firstAllowed) return <div className="p-8 text-center text-gray-500">You do not have access to any sections.</div>;
      const redirectPath = firstAllowed === 'dashboard' ? '/dashboard' : `/dashboard/${firstAllowed}`;
      navigate(redirectPath, { replace: true });
      return null;
    }
    const isLimitedRole = currentUser?.role === 'user' || currentUser?.role === 'viewer';
    const isScorerRole = currentUser?.role === 'scorer';
    const isEditorRole = currentUser?.role === 'editor';
    const isManagerRole = currentUser?.role === 'manager';
    const isTechnicalRole = currentUser?.role === 'technical';

    switch (activeTab) {
      case 'dashboard':
        if (isScorerRole) return <ScorerDashboardHome tournamentId={selectedTournament.id} />;
        if (isLimitedRole) return <UserDashboardHome tournamentId={selectedTournament.id} />;
        if (isEditorRole) return <EditorDashboardHome tournamentId={selectedTournament.id} />;
        if (isManagerRole) return <ManagerDashboardHome tournamentId={selectedTournament.id} />;
        if (isTechnicalRole) return <TechnicalDashboardHome tournamentId={selectedTournament.id} />;
        return <DashboardHome tournamentId={selectedTournament.id} />;
      case 'players':
        return renderPlayerScoresSubTab('players');
      case 'scores':
        return renderPlayerScoresSubTab('scores');
      case 'partners':
        return <Partners tournamentId={selectedTournament.id} />;
      case 'calendar':
        return <EventCalendar tournamentId={selectedTournament.id} />;
      case 'finance':
        return <Finance tournamentId={selectedTournament.id} />;
      case 'contents':
        return <Contents selectedPage={selectedPage} handleBackToOverview={handleBackToOverview} />;
      case 'tournaments':
        return <Tournaments onSelectTournament={handleTournamentChange} />;
      case 'courses':
        return <Courses />;
      case 'settings':
        return <Settings />;
      case 'press':
        return <PressManagement />;
      case 'analytics':
        return <Analytics />;
      case 'messaging':
        return <Messaging tournamentId={selectedTournament.id} />;
      case 'interest':
        return <InterestSubmissions />;
      default:
        return <DashboardHome tournamentId={selectedTournament.id} />;
    }
  };

  if (authChecking) {
    return (
      <div className="flex items-center justify-center h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent" />
      </div>
    );
  }

  if (authStatus === 'pending') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-amber-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-golf-gray mb-3">Access Not Configured</h1>
          <p className="text-gray-500 mb-6">Your account exists but does not have a recognized role assigned. Please contact an administrator to assign the correct role to your account.</p>
          <button
            onClick={() => { supabase.auth.signOut(); navigate('/'); }}
            className="px-6 py-3 bg-golf-orange text-white rounded-lg font-medium hover:bg-golf-orange/90 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  if (authStatus === 'denied') {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-golf-gray mb-3">Access Denied</h1>
          <p className="text-gray-500 mb-6">Your account has been deactivated. Please contact an administrator for assistance.</p>
          <button
            onClick={() => { supabase.auth.signOut(); navigate('/'); }}
            className="px-6 py-3 bg-golf-orange text-white rounded-lg font-medium hover:bg-golf-orange/90 transition-colors"
          >
            Sign Out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="fixed top-0 left-0 right-0 z-40 h-20 bg-white" />

      <DashboardHeader
        tournaments={tournaments}
        selectedTournament={selectedTournament}
        onTournamentChange={handleTournamentChange}
        activeTab={activeTab}
        onTabChange={handleTabChange}
        allowedTabs={currentUser?.allowedTabs as AdminTab[] | undefined}
        tabsLoading={userLoading}
      />

      <main className="container mx-auto px-4 pt-40 pb-8">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-golf-orange border-t-transparent"></div>
          </div>
        ) : (
          <motion.div
            key={`${activeTab}-${selectedTournament.id}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            {renderContent()}
          </motion.div>
        )}
      </main>
    </div>
  );
}

export default Dashboard;
